import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cx } from "./primitives";

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for browsers without async clipboard access (older WebViews, insecure contexts).
    try {
      const el = document.createElement("textarea");
      el.value = text;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * Copies `value` and announces the result to screen readers. `label` names what is copied.
 * `compact` renders an icon-only button for use next to inline IDs.
 */
export function CopyButton({
  value,
  label,
  className,
  compact = false,
  idleText = "Copy",
}: {
  value: string;
  label: string;
  className?: string;
  compact?: boolean;
  /** Visible text before copying (full-size button only). */
  idleText?: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function onClick() {
    const ok = await copyText(value);
    setState(ok ? "copied" : "failed");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 2000);
  }

  const text = state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : idleText;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy ${label}`}
      title={compact ? `Copy ${label}` : undefined}
      className={cx(
        "inline-flex shrink-0 items-center justify-center gap-1 rounded-md font-medium text-fg-muted hover:bg-subtle hover:text-fg",
        compact ? "size-6" : "h-7 border border-line-strong bg-surface px-2 text-xs text-fg shadow-xs",
        className,
      )}
    >
      {state === "copied" ? (
        <Check aria-hidden="true" className="size-3.5 text-ok" />
      ) : (
        <Copy aria-hidden="true" className="size-3.5" />
      )}
      <span aria-live="polite" className={compact ? "sr-only" : undefined}>
        {compact && state === "idle" ? "" : text}
      </span>
    </button>
  );
}
